import React, { useState, useRef } from 'react';
import ApiService from '../../../_services/ApiService';
import { parseExcelFile, parseSlotsSheet, parsePatternSheet, ParsedSlotRow, ParsedPatternRow } from '../utils/excelParser';
import { validateSlotRows, validatePatternRows, ValidationError } from '../utils/excelValidator';
import { generateSlotsTemplate, generatePatternTemplate } from '../utils/excelTemplateGenerator';
import ImportPreviewTable from './ImportPreviewTable';

interface ExcelImportModalProps {
  templateId: number;
  template: {
    startDate: string;
    endDate: string;
  };
  importType: 'slots' | 'pattern';
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'upload' | 'preview' | 'importing' | 'result';

interface ImportResult {
  inserted: number;
  skipped: number;
  errors: Array<{ row: number; field?: string; message: string }>;
}

const ExcelImportModal: React.FC<ExcelImportModalProps> = ({
  templateId,
  template,
  importType,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<Step>('upload');
  const [mode, setMode] = useState<'add' | 'replace'>(importType === 'slots' ? 'add' : 'replace');
  const [rows, setRows] = useState<(ParsedSlotRow | ParsedPatternRow)[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validCount = rows.filter((_, i) => !validationErrors.some(e => e.row === i)).length;

  // Handle closing the modal - call onSuccess if we're on result step
  const handleClose = () => {
    if (step === 'result') {
      onSuccess();
    }
    onClose();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const buffer = await file.arrayBuffer();
      const workbook = parseExcelFile(buffer);

      let parsedRows: (ParsedSlotRow | ParsedPatternRow)[];
      let errors: ValidationError[];

      if (importType === 'slots') {
        parsedRows = parseSlotsSheet(workbook);
        errors = validateSlotRows(
          parsedRows as ParsedSlotRow[],
          template.startDate,
          template.endDate
        );
      } else {
        parsedRows = parsePatternSheet(workbook);
        errors = validatePatternRows(parsedRows as ParsedPatternRow[]);
      }

      if (parsedRows.length === 0) {
        setImportError('No data rows found in the Excel file. Make sure the first sheet has column headers (Date, Start Time, End Time, etc.).');
        return;
      }

      setRows(parsedRows);
      setValidationErrors(errors);
      setImportError(null);
      setStep('preview');
    } catch (err: any) {
      setImportError(`Failed to parse Excel file: ${err.message}`);
    }

    // Reset file input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      const response = await ApiService.getReferenceData(templateId);
      const { instructors, topics, batches } = response.data.data;

      if (importType === 'slots') {
        generateSlotsTemplate(instructors, topics, batches);
      } else {
        generatePatternTemplate(instructors, topics);
      }
    } catch (err: any) {
      setImportError(`Failed to download template: ${err.message}`);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleImport = async () => {
    // Collect only valid rows
    const validRows = rows.filter((_, i) => !validationErrors.some(e => e.row === i));

    if (validRows.length === 0) return;

    setStep('importing');
    setImportError(null);

    try {
      let response;
      if (importType === 'slots') {
        response = await ApiService.bulkImportSlots(templateId, {
          slots: validRows,
          mode,
        });
      } else {
        response = await ApiService.bulkImportPattern(templateId, {
          pattern: (validRows as ParsedPatternRow[]).map(r => ({
            ...r,
            // Normalize time to HH:mm for pattern
            startTime: r.startTime,
            endTime: r.endTime,
          })),
          mode,
        });
      }

      if (response.data.success) {
        setImportResult(response.data.data);
        setStep('result');
        // Don't call onSuccess() here - wait for user to close the modal
      } else {
        setImportError(response.data.error || 'Import failed');
        setStep('preview');
      }
    } catch (err: any) {
      const detail = err.response?.data?.message || err.message;
      setImportError(err.response?.data?.error || `Import failed: ${detail}`);
      setStep('preview');
    }
  };

  return (
    <div className="modal-overlay" role="presentation" onClick={handleClose}>
      <div className="modal-content excel-import-modal" role="dialog" aria-modal="true" aria-labelledby="excel-import-title" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 id="excel-import-title">Import {importType === 'slots' ? 'Activity Slots' : 'Week Pattern'} from Excel</h3>
          <button className="modal-close" onClick={handleClose}>&times;</button>
        </div>

        <div className="modal-body">
          {importError && (
            <div className="import-error-banner">{importError}</div>
          )}

          {/* Step: Upload */}
          {step === 'upload' && (
            <div className="import-upload-step">
              <div className="import-mode-toggle">
                <label className="import-mode-label">Import mode:</label>
                <label className="import-radio">
                  <input
                    type="radio"
                    name="import-mode"
                    checked={mode === 'add'}
                    onChange={() => setMode('add')}
                  />
                  Add to existing
                </label>
                <label className="import-radio">
                  <input
                    type="radio"
                    name="import-mode"
                    checked={mode === 'replace'}
                    onChange={() => setMode('replace')}
                  />
                  Replace all
                </label>
              </div>

              {mode === 'replace' && (
                <div className="import-replace-warning">
                  Warning: Replace mode will delete all existing {importType === 'slots' ? 'date-specific slots' : 'pattern entries'} before importing.
                </div>
              )}

              <div className="import-file-section">
                <p className="import-instructions">
                  Select an Excel file (.xlsx) to import. The first sheet should contain the data
                  with column headers: {importType === 'slots'
                    ? 'Date, Start Time, End Time, Instructor ID/Name, Topic ID/Name, Batches, Is Online, Agenda'
                    : 'Day, Start Time, End Time, Instructor ID/Name, Topic ID/Name, Agenda'}
                </p>

                <div className="import-file-actions">
                  <label className="btn btn-primary import-file-btn">
                    Choose Excel File
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                  </label>

                  <button
                    className="btn btn-secondary"
                    onClick={handleDownloadTemplate}
                    disabled={downloadingTemplate}
                  >
                    {downloadingTemplate ? 'Downloading...' : 'Download Template'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step: Preview */}
          {step === 'preview' && (
            <div className="import-preview-step">
              <ImportPreviewTable
                rows={rows}
                errors={validationErrors}
                importType={importType}
              />
            </div>
          )}

          {/* Step: Importing */}
          {step === 'importing' && (
            <div className="import-loading-step">
              <div className="import-spinner" />
              <p>Importing {validCount} rows...</p>
            </div>
          )}

          {/* Step: Result */}
          {step === 'result' && importResult && (
            <div className="import-result-step">
              <div className="import-result-summary">
                <p className="import-result-success">
                  Successfully imported {importResult.inserted} {importType === 'slots' ? 'slots' : 'pattern entries'}.
                </p>
                {importResult.skipped > 0 && (
                  <p className="import-result-skipped">
                    {importResult.skipped} rows were skipped due to errors.
                  </p>
                )}
              </div>
              {importResult.errors.length > 0 && (
                <div className="import-result-errors">
                  <h4>Server-side errors:</h4>
                  <ul>
                    {importResult.errors.map((err, i) => (
                      <li key={i}>Row {err.row}: {err.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step === 'upload' && (
            <button className="btn btn-secondary" onClick={handleClose}>Cancel</button>
          )}
          {step === 'preview' && (
            <>
              <button className="btn btn-secondary" onClick={() => { setStep('upload'); setRows([]); setValidationErrors([]); }}>
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={handleImport}
                disabled={validCount === 0}
              >
                Import {validCount} {validCount === 1 ? 'row' : 'rows'}
              </button>
            </>
          )}
          {step === 'result' && (
            <button className="btn btn-primary" onClick={handleClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelImportModal;
